import Cookies from "js-cookie";
import * as React from "react";
import { UserContext, Constants } from "@socialcoach/services";
import { PropsWithChildren, useState, useEffect, useCallback, useMemo } from "react";
import { SocialCoachSessionService } from "@/services";
import { SessionServices, SocialCoachSession } from "@/services/bedrock";

type State = {
  userContext?: UserContext;
  session?: SocialCoachSession;
  clearSession: () => void;
  refreshSession: () => Promise<void>;
  loading: boolean;
};

export const SessionContext = React.createContext<State>({
  loading: false,
  clearSession: () => null,
  refreshSession: async () => {
    /* default */
  },
  userContext: undefined,
  session: undefined,
});

type Props = {
  userContext?: UserContext;
  session?: SocialCoachSession;
  loading?: boolean;
};

export const SessionContextProvider: React.FC<PropsWithChildren<Props>> = ({
  children,
  userContext: initialUserContext,
  session: initialSession,
  loading: initialLoading,
}) => {
  const [userContext, setUserContext] = useState<UserContext | undefined>(initialUserContext);
  const [session, setSession] = useState<SocialCoachSession | undefined>(initialSession);
  const [loading, setLoading] = useState<boolean>(initialLoading === undefined ? true : initialLoading);

  const clearSession = useCallback((): void => {
    Cookies.remove(Constants.COOKIE_NAME);
    setUserContext(undefined);
    setSession(undefined);
  }, []);

  const checkForSession = useCallback(async (): Promise<void> => {
    const isCookieSessionAvailable = !!Cookies.get(Constants.COOKIE_NAME);
    if (isCookieSessionAvailable) {
      await Promise.allSettled([
        SocialCoachSessionService.getUserContext()
          .then(setUserContext)
          .catch(e => {
            setUserContext(undefined);
            console.warn("Error getting userContext: ", e);
          }),
        SessionServices.getMe()
          .then(setSession)
          .catch(e => {
            setSession(undefined);
            console.warn("Error getting session data: ", e);
          }),
      ]);
    }
    setLoading(false);
  }, []);

  const refreshSession = useCallback(async (): Promise<void> => {
    setLoading(true);
    await checkForSession();
  }, [checkForSession]);

  useEffect(() => {
    checkForSession().catch(console.error);
  }, [checkForSession]);

  const contextValue = useMemo(
    () => ({
      userContext,
      session,
      clearSession,
      refreshSession,
      loading,
    }),
    [userContext, clearSession, refreshSession, loading, session]
  );

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};
